import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import type { ReleaseNote } from '@kangaroo/shared';

import { WEB_APP_VERSION } from '../lib/appVersion';

type WhatsNewDialogProps = {
  releaseNotes: ReleaseNote[];
  onDismiss: (releaseKeys: string[]) => Promise<void>;
};

function formatReleaseDate(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function WhatsNewDialog({ releaseNotes, onDismiss }: WhatsNewDialogProps) {
  const [dismissing, setDismissing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleDismiss() {
    if (releaseNotes.length === 0 || dismissing) {
      return;
    }

    setDismissing(true);
    setError(null);
    try {
      await onDismiss(releaseNotes.map((note) => note.releaseKey));
    } catch (dismissError) {
      setError(dismissError instanceof Error ? dismissError.message : 'Could not save dismissal.');
    } finally {
      setDismissing(false);
    }
  }

  return (
    <Dialog open={releaseNotes.length > 0} onClose={() => void handleDismiss()} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1.25 }}>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: 1,
              display: 'grid',
              placeItems: 'center',
              bgcolor: 'rgba(124,58,237,0.16)',
              color: '#C4B5FD',
            }}
          >
            <AutoAwesomeOutlinedIcon />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: '1.22rem', fontWeight: 900, lineHeight: 1.15 }}>
              What&apos;s new
            </Typography>
            {releaseNotes.length > 0 ? (
              <Typography sx={{ fontSize: '0.82rem', color: 'text.secondary', mt: 0.25 }}>
                {releaseNotes.length === 1 ? '1 unread release' : `${releaseNotes.length} unread releases`}
              </Typography>
            ) : null}
          </Box>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={1.4}>
          {error ? <Alert severity="error">{error}</Alert> : null}
          {releaseNotes.map((releaseNote, index) => (
            <Box
              key={releaseNote.releaseKey}
              sx={{
                pt: index === 0 ? 0 : 2,
                borderTop: index === 0 ? 0 : '1px solid',
                borderColor: 'divider',
              }}
            >
              <Stack spacing={1.15}>
                <Box>
                  <Typography sx={{ fontSize: '1.02rem', fontWeight: 850 }}>
                    {releaseNote.title}
                  </Typography>
                  <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
                    {formatReleaseDate(releaseNote.publishedAt)}
                  </Typography>
                </Box>
                {releaseNote.summary ? (
                  <Typography sx={{ fontSize: '0.94rem', color: 'text.secondary', lineHeight: 1.55 }}>
                    {releaseNote.summary}
                  </Typography>
                ) : null}
                <Stack spacing={0.8}>
                  {releaseNote.items.map((item) => (
                    <Stack key={item} direction="row" spacing={0.85} alignItems="flex-start">
                      <CheckCircleOutlineIcon sx={{ fontSize: 18, color: '#8B5CF6', mt: 0.15, flexShrink: 0 }} />
                      <Typography sx={{ fontSize: '0.9rem', lineHeight: 1.45 }}>
                        {item}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
                <Chip
                  size="small"
                  label={`Release ${releaseNote.releaseKey}`}
                  sx={{ alignSelf: 'flex-start', fontWeight: 800, bgcolor: 'rgba(124,58,237,0.14)', color: '#C4B5FD' }}
                />
              </Stack>
            </Box>
          ))}
          <Chip size="small" variant="outlined" label={`Web ${WEB_APP_VERSION}`} sx={{ alignSelf: 'flex-start' }} />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.25 }}>
        <Button
          variant="contained"
          onClick={() => void handleDismiss()}
          disabled={releaseNotes.length === 0 || dismissing}
          sx={{ textTransform: 'none', fontWeight: 800 }}
        >
          {dismissing ? 'Saving...' : "Got it, mark all as read"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
